"use client"

import NextLink from "next/link"
import { forwardRef, useState } from "react"

/**
 * Link component with intent-based prefetching.
 *
 * Prefetches routes on hover (desktop) or touchstart (mobile/tablet) instead of
 * when entering the viewport. This reduces server load while maintaining good UX
 * for both input types.
 *
 * Uses Link's built-in prefetch mechanism (prefetch={null}) which is more efficient
 * than router.prefetch() and benefits from Next.js 16's incremental prefetching and
 * layout deduplication optimizations.
 *
 * This component wraps next/link and can be used as a drop-in replacement.
 * It can also be used with styled-components.
 *
 * @example
 * ```jsx
 * import { IntentPrefetchLink } from '@/components/Link'
 *
 * // Basic usage
 * <IntentPrefetchLink href="/dashboard">Dashboard</IntentPrefetchLink>
 *
 * // With styled-components
 * const StyledLink = styled(IntentPrefetchLink)`
 *   color: blue;
 * `
 * ```
 */
export const IntentPrefetchLink = forwardRef(function IntentPrefetchLink(
  { href, prefetch, children, onMouseEnter, onTouchStart, ...props },
  ref,
) {
  const [shouldPrefetch, setShouldPrefetch] = useState(false)

  const handleIntent = () => {
    if (!shouldPrefetch && prefetch !== false) {
      // Enable prefetching by setting prefetch to null (restores default behavior)
      // This uses Link's optimized prefetching which benefits from Next.js 16's
      // incremental prefetching and layout deduplication
      setShouldPrefetch(true)
    }
  }

  // Merge with any existing handlers that might be passed in props
  const handleMouseEnter = (e) => {
    handleIntent()
    onMouseEnter?.(e)
  }

  const handleTouchStart = (e) => {
    handleIntent()
    onTouchStart?.(e)
  }

  // If prefetch is explicitly false, respect it
  // Otherwise: false initially (no viewport prefetch), null on intent (enables prefetch)
  // null restores Link's default prefetch behavior which is optimized in Next.js 16
  const prefetchValue = prefetch === false ? false : shouldPrefetch ? null : false

  return (
    <NextLink
      ref={ref}
      href={href}
      prefetch={prefetchValue}
      onMouseEnter={handleMouseEnter}
      onTouchStart={handleTouchStart}
      {...props}
    >
      {children}
    </NextLink>
  )
})

IntentPrefetchLink.displayName = "IntentPrefetchLink"
